import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { PageHeader } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './ViewVoucherThemes.module.scss';
import DataTable from '../../components/DataTable/DataTable';
import { Pagination } from '../../components';
import EditVoucherThemeModal from '../../components/Modals/EditVoucherThemeModal';
import { getVoucherThemes, getVoucherThemeCategories, voucherThemeSearchApi } from '../../api/voucherTheme';
import Search from '../../components/Search/Search.component';
import { APP_PARAMS } from '../../constants/appParams';
import { useGetQueryParams } from '../../hooks/useGetQueryParams';
import { useSetQueryParams } from '../../hooks/useSetQueryParams';
import { Dropdown, DropdownButton, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';

const ViewVoucherThemes = (props) => {
  const queryParams = useGetQueryParams([
    APP_PARAMS.PAGE,
    APP_PARAMS.SEARCH_KEY,
    'searchType',
  ]);
  const setQueryParam = useSetQueryParams();

  const [state, setState] = useComponentState({
    tableHeading: {
      column: [
        'Voucher Theme ID',
        'Theme Name',
        'Category',
        'Front Image',
        'Back Image',
        'SMS Message',
        'Email Message',
      ],
    },
    voucherThemes: [],
    loading: false,
    notFoundMessage: '',
    fetchError: '',
    expanded: {},
    currentPage: queryParams.page - 1 || 0,
    totalPages: 0,
    showEditModal: false,
    themeToEdit: null,
    categories: [],
    searchKey: queryParams.searchKey || '',
    searchType: queryParams.searchType || 'themeName',
    isClearable: false,
    isCallApi: true,
    pageSize: 5,
  });

  const {
    voucherThemes,
    tableHeading,
    loading,
    notFoundMessage,
    fetchError,
    expanded,
    currentPage,
    totalPages,
    showEditModal,
    themeToEdit,
    categories,
    searchKey,
    searchType,
    isClearable,
    isCallApi,
    pageSize,
  } = state;

  const fetchVoucherThemes = async (pageNumber = currentPage) => {
    setState({
      loading: true,
      voucherThemes: [],
      fetchError: '',
      notFoundMessage: '',
    });

    try {
      let response;

      // If there's a search query, use search API, otherwise get all themes
      if (searchKey || queryParams.searchKey) {
        const searchKeyValue = searchKey || queryParams.searchKey;
        const searchIdValue = searchType === 'themeName' ? 'name' : 'category';

        response = await voucherThemeSearchApi(
          props.auth.auth.token,
          searchKeyValue,
          searchIdValue,
          pageNumber,
          pageSize
        );
      } else {
        response = await getVoucherThemes(
          props.auth.auth.token,
          pageNumber,
          pageSize
        );
      }

      if (response && response.list.length > 0) {
        setState({
          voucherThemes: response.list,
          totalPages: response.totalPageCount,
          currentPage: pageNumber,
        });
      } else {
        setState({
          notFoundMessage: 'No voucher themes found',
        });
      }
    } catch (error) {
      setState({
        fetchError:
          error?.response?.status === 403
            ? 'Access denied. Please check your permissions.'
            : 'Failed to load voucher themes',
      });
    }
    setState({ loading: false });
  };

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getVoucherThemeCategories(props.auth.auth.token);
        if (response && Array.isArray(response)) {
          setState({
            categories: response.voucherThemeCategories,
          });
        }
      } catch (error) {
        toast.error('Failed to load categories:', error);
      }
    };
    fetchCategories();
    // eslint-disable-next-line
  }, []);
  const handleThemesPagination = (selectedPage) => {
    setState({
      currentPage: selectedPage,
    });
    fetchVoucherThemes(selectedPage);
  };

  // Handle search input change
  const handleSearchChange = (value) => {
    setState({
      isCallApi: false,
      searchKey: value,
      isClearable: false,
    });
  };

  // Handle search button press
  const handleSearch = () => {
    if (!isClearable || queryParams.searchKey) {
      if (searchKey || queryParams.searchKey) {
        setQueryParam({
          [APP_PARAMS.SEARCH_KEY]: searchKey || queryParams.searchKey,
          [APP_PARAMS.PAGE]: 1,
          searchType: searchType,
        });
        setState({
          searchKey: searchKey || queryParams.searchKey,
          isClearable: true,
          isCallApi: true,
        });
      }
    } else {
      handleSearchClear();
    }
  };

  // Handle search clear
  const handleSearchClear = () => {
    setQueryParam({
      [APP_PARAMS.SEARCH_KEY]: null,
      searchType: null,
      [APP_PARAMS.PAGE]: 1,
    });
    setState({
      searchKey: '',
      searchType: 'themeName',
      isClearable: false,
      notFoundMessage: null,
    });
  };


  // Handle search type change (Theme Name or Category)
  const handleSearchTypeChange = (type) => {
    setState({
      searchType: type,
      searchKey: '',
      isClearable: false,
      isCallApi: false, // Prevent API call when changing search type
    });
  };

  // Render search type dropdown
  const renderSearchTypeDropdown = () => {
    return (
      <DropdownButton
        as={InputGroup.Prepend}
        variant='primary'
        title={searchType === 'themeName' ? 'Theme Name' : 'Category'}
        id='input-group-dropdown-search-type'
        className={styles.searchTypeDropdown}
      >
        <Dropdown.Item
          onClick={() => handleSearchTypeChange('themeName')}
        >
          Theme Name
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => handleSearchTypeChange('category')}
        >
          Category
        </Dropdown.Item>
      </DropdownButton>
    );
  };

  // Dependencies for useEffect
  useEffect(() => {
    if (!searchKey && !queryParams.searchKey) {
      fetchVoucherThemes(0);
      setQueryParam({
        [APP_PARAMS.PAGE]: queryParams.page || 1,
      });
    } else if ((searchKey || queryParams.searchKey) && isCallApi) {
      fetchVoucherThemes(0);
    }
    // eslint-disable-next-line
  }, [searchKey, isCallApi]);

  // Render image cell for front/back images
  const renderVoucherThemeImageCell = (imageUrl) => (
    <img
      src={imageUrl}
      alt='Theme preview'
      style={{
        width: '300px',
        height: '300px',
        objectFit: 'scale-down',
        borderRadius: '4px',
      }}
    />
  );

  // Handlers for edit modal
  const handleEditVoucherTheme = (theme) => {
    setState({
      showEditModal: true,
      themeToEdit: theme,
    });
  };

  const handleEditModalClose = () => {
    setState({
      showEditModal: false,
      themeToEdit: null,
    });
  };

  const handleVoucherUpdateSuccess = () => {
    fetchVoucherThemes(currentPage);
  };

  // Truncate text and provide expand/collapse option for SMS/Email content
  const renderExpandableText = (text, themeId, field, length = 100) => {
    if (!text) return '';
    const key = `${themeId}_${field}`;
    const isExpanded = expanded[key];

    if (isExpanded) {
      // Show full text with "Read Less" option
      return (
        <span>
          {text}{' '}
          <button
            type="button"
            onClick={() => {
              setState({
                expanded: {
                  ...expanded,
                  [key]: false,
                },
              });
            }}
            className={styles.readMoreLess}
            style={{ background: 'none', border: 'none', padding: 0, color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
          >
            ...Read Less
          </button>
        </span>
      );
    }

    // Show truncated text with "Read More" option
    return text.length > length ? (
      <span title={text}>
        {text.substring(0, length)}...{' '}
        <button
          type="button"
          onClick={() => {
            setState({
              expanded: {
                ...expanded,
                [key]: true,
              },
            });
          }}
          className={styles.readMoreLess}
          style={{ background: 'none', border: 'none', padding: 0, color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Read More
        </button>
      </span>
    ) : (
      text
    );
  };

  const flatVoucherThemes = voucherThemes
    .filter(
      (group) =>
        Array.isArray(group.voucherThemes) && group.voucherThemes.length > 0
    )
    .flatMap((group) => group.voucherThemes);

  return (
    <div className={styles.root_div}>
      <PageHeader text='Voucher Themes' />

      {/* Search by Theme Name or Category */}
      <div className={styles.filter_Search_Container}>
        {renderSearchTypeDropdown()}

        <Search
          placeholder={searchType === 'themeName' ? 'Enter Theme Name' : 'Enter Category'}
          onBtnPress={handleSearch}
          onClearPress={handleSearchClear}
          onChange={handleSearchChange}
          max={50}
          handleValueFromParent
          value={searchKey}
          searchKey={queryParams.searchKey}
        />
      </div>

      {/* Data table for voucher themes */}
      <div className={`${styles.voucherTableWrapper} table-fit`}>
        <DataTable
          tableData={flatVoucherThemes}
          tableheading={tableHeading}
          tag='voucherThemes'
          renderVoucherThemeImageCell={renderVoucherThemeImageCell}
          renderExpandableText={renderExpandableText}
          expanded={expanded}
          fetchError={fetchError}
          search_filter_loading={loading}
          handleEditVoucherTheme={handleEditVoucherTheme}
        />
      </div>
      {/* Pagination controls */}
      {!loading && !fetchError && (
        <Pagination
          pageCount={totalPages}
          onChange={handleThemesPagination}
          currentPage={currentPage}
        />
      )}

      {/* Edit voucher theme modal */}
      <EditVoucherThemeModal
        show={showEditModal}
        onHide={handleEditModalClose}
        theme={themeToEdit}
        token={props.auth.auth.token}
        onSuccess={handleVoucherUpdateSuccess}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(ViewVoucherThemes);
